/**
 * @file consent-texts-fields.ts
 * @description Раскладка полей формы текстов согласия по вкладкам и группам
 * (phase19.16, Р3г; uiux-r3g-consent-texts §3). Пределы, вставки, подписи
 * для сводки и «в каких видах баннера видно» — в shared `CONSENT_TEXT_FIELDS`;
 * здесь только то, что нужно вёрстке: вкладка, группа, короткая подпись в
 * группе, подсказка, многострочность. Новое поле = запись (тест сверяет
 * покрытие реестра shared).
 */

import { CONSENT_TEXT_KINDS, type ConsentTextKind } from "@chaiberi/shared";

/** Вкладка формы. */
export type ConsentTextsTab = "banner" | "settings" | "buttons" | "risk";

export interface ConsentTabMeta {
  value: ConsentTextsTab;
  label: string;
  /** Какую редакцию правит вкладка: у окна о риске своя нумерация (db-design §1). */
  kind: ConsentTextKind;
}

/** Вкладки в порядке показа (uiux §3). */
export const CONSENT_TEXT_TABS: readonly ConsentTabMeta[] = [
  { value: "banner", label: "Баннер", kind: "BANNER" },
  { value: "settings", label: "Окно «Настроить»", kind: "BANNER" },
  { value: "buttons", label: "Кнопки и подвал", kind: "BANNER" },
  { value: "risk", label: "Окно о риске", kind: "NOTICE_RISK" },
];

/** Первая вкладка вида — с неё открывается форма. */
export function firstTabOf(kind: ConsentTextKind): ConsentTextsTab {
  return CONSENT_TEXT_TABS.find((t) => t.kind === kind)?.value ?? "banner";
}

export interface ConsentFieldLayout {
  path: string;
  kind: ConsentTextKind;
  tab: ConsentTextsTab;
  /** Подзаголовок группы внутри вкладки. */
  group?: string;
  label: string;
  hint?: string;
  multiline?: boolean;
}

const B = "BANNER" as const;
const R = "NOTICE_RISK" as const;
const MODE_CONSENT = "Режим «Только после согласия»";
const MODE_NOTICE = "Режим «Уведомление»";

/** Поля по порядку на вкладках. */
export const CONSENT_FIELD_LAYOUT: readonly ConsentFieldLayout[] = [
  // Баннер
  { path: "introDefault", kind: B, tab: "banner", label: "Вступление по умолчанию", hint: "Если владелец своего не написал. Без точки в конце", multiline: true },
  { path: "consentPhrase", kind: B, tab: "banner", label: "Фраза «Только после согласия»", multiline: true },
  { path: "noticePhrase", kind: B, tab: "banner", label: "Фраза «Уведомление»", multiline: true },
  { path: "compactConsent", kind: B, tab: "banner", label: "Компактная полоса «после согласия»", hint: "Оформление заказа и личный кабинет — две строки", multiline: true },
  { path: "compactNotice", kind: B, tab: "banner", label: "Компактная полоса «Уведомление»", multiline: true },
  { path: "info", kind: B, tab: "banner", label: "Без счётчиков", hint: "Магазинам без Метрики и VK — без слов о счётчиках", multiline: true },
  { path: "more", kind: B, tab: "banner", label: "Ссылка на политику ПДн" },
  // Окно «Настроить»
  { path: "settingsTitle", kind: B, tab: "settings", group: "Окно", label: "Заголовок окна" },
  { path: "alwaysOn", kind: B, tab: "settings", group: "Окно", label: "Подпись «Всегда включены»" },
  { path: "necessary.label", kind: B, tab: "settings", group: "Необходимые", label: "Название" },
  { path: "necessary.description", kind: B, tab: "settings", group: "Необходимые", label: "Описание", multiline: true },
  { path: "categories.analytics.label", kind: B, tab: "settings", group: "Аналитика", label: "Название" },
  { path: "categories.analytics.description", kind: B, tab: "settings", group: "Аналитика", label: "Описание", multiline: true },
  { path: "analyticsWebvisorDescription", kind: B, tab: "settings", group: "Аналитика", label: "Описание, когда включён Вебвизор", multiline: true },
  { path: "categoryGenitive.analytics", kind: B, tab: "settings", group: "Аналитика", label: "Во фразе: аналитики", hint: "Без него не заполнить {categories}: «сервисы аналитики»" },
  { path: "categories.ads.label", kind: B, tab: "settings", group: "Реклама", label: "Название" },
  { path: "categories.ads.description", kind: B, tab: "settings", group: "Реклама", label: "Описание", multiline: true },
  { path: "categoryGenitive.ads", kind: B, tab: "settings", group: "Реклама", label: "Во фразе: рекламы", hint: "Без него не заполнить {categories}: «сервисы рекламы»" },
  // Кнопки и подвал
  { path: "buttons.acceptAll", kind: B, tab: "buttons", group: MODE_CONSENT, label: "«Принять все»" },
  { path: "buttons.necessaryOnly", kind: B, tab: "buttons", group: MODE_CONSENT, label: "«Только необходимые»" },
  { path: "buttons.configure", kind: B, tab: "buttons", group: MODE_CONSENT, label: "«Настроить»", hint: "Эта же кнопка — в режиме «Уведомление»" },
  { path: "buttons.ok", kind: B, tab: "buttons", group: MODE_NOTICE, label: "«Понятно»", hint: "Эта же кнопка — у магазинов без счётчиков" },
  { path: "buttons.refuse", kind: B, tab: "buttons", group: MODE_NOTICE, label: "«Отказаться»" },
  { path: "buttons.save", kind: B, tab: "buttons", group: "Окно «Настроить»", label: "«Сохранить выбор»" },
  { path: "settingsLink", kind: B, tab: "buttons", group: "Подвал", label: "Ссылка «Настройки cookie»" },
  // Окно о риске
  { path: "title", kind: R, tab: "risk", label: "Заголовок" },
  { path: "body", kind: R, tab: "risk", label: "Текст", multiline: true },
  { path: "acknowledge", kind: R, tab: "risk", label: "Подпись галочки", multiline: true },
];

/** Поля вкладки по порядку. */
export function fieldsOfTab(tab: ConsentTextsTab): ConsentFieldLayout[] {
  return CONSENT_FIELD_LAYOUT.filter((f) => f.tab === tab);
}

/** Вкладка поля (неизвестный путь — первая вкладка вида). */
export function consentTabOf(kind: ConsentTextKind, path: string): ConsentTextsTab {
  return CONSENT_FIELD_LAYOUT.find((f) => f.kind === kind && f.path === path)?.tab ?? firstTabOf(kind);
}

/** id поля в DOM — для ссылок сводки ошибок и подсветки. */
export function consentFieldDomId(path: string): string {
  return `consent-field-${path.replace(/\./gu, "-")}`;
}

/** Вид из адреса `?kind=`; неизвестный — баннер. */
export function kindFromParam(value: string | null): ConsentTextKind {
  return CONSENT_TEXT_KINDS.find((k) => k === value) ?? "BANNER";
}
